import {differenceBy} from 'lodash-es'

import type {MemexItemModel} from '../models/memex-item-model'

/**
 * Checks if a specific part of the model has changed between two versions.
 *
 * @example
 * const hasAssigneesChanged = hasModelChanged(prevModel, currentModel, model => model.columns?.Assignees);
 *
 * @param current The previous/current version of the model
 * @param next The new/next version of the model
 * @param accessor Function to extract the property to compare from the model
 * @returns True if the specified part of the model has changed, false otherwise
 */
export const hasModelChanged = <TCurrent extends Partial<MemexItemModel>, UNext extends Partial<MemexItemModel>, R>(
  current: TCurrent | undefined,
  next: UNext | undefined,
  accessor: (model: TCurrent | UNext) => R,
): boolean => {
  const currentValue = current ? accessor(current) : undefined
  const nextValue = next ? accessor(next) : undefined

  if (Array.isArray(currentValue) && Array.isArray(nextValue)) {
    if (currentValue.length !== nextValue.length) {
      return true
    }

    const newItems = differenceBy(nextValue, currentValue, 'id')
    return newItems.length > 0
  }

  return currentValue !== nextValue
}
